import { type CountryCode, type PhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';

/** Default country set when callers don't specify one — preserves the historical US-only behavior. */
export const DEFAULT_ALLOWED_COUNTRIES: CountryCode[] = ['US'];

/**
 * Country set used for outbound voice/SMS blocks (callee, transfer destinations, SMS recipient).
 * Centralized so widening the scope (e.g. add UK) is a one-line change.
 */
export const OUTBOUND_CALL_AND_SMS_ALLOWED_COUNTRIES: CountryCode[] = ['US', 'CA'];

const COUNTRY_CODE_DISPLAY_NAMES: Partial<Record<CountryCode, string>> = {
  US: 'US',
  CA: 'Canadian',
};

/**
 * Builds an "Invalid …" label suitable for both Zod messages (caller appends `.`) and runtime
 * error messages (caller appends `: <number>`). Single country: `Invalid US phone number`.
 * Multiple: `Invalid phone number (must be a valid US, Canadian, or GB phone number)`.
 */
export function buildInvalidPhoneNumberLabel(allowedCountries: CountryCode[]): string {
  const displayNames = allowedCountries.map((code) => COUNTRY_CODE_DISPLAY_NAMES[code] ?? code);
  if (displayNames.length === 1) {
    return `Invalid ${displayNames[0]} phone number`;
  }
  if (displayNames.length === 2) {
    return `Invalid phone number (must be a valid ${displayNames.join(' or ')} phone number)`;
  }
  return `Invalid phone number (must be a valid ${displayNames.slice(0, -1).join(', ')}, or ${displayNames[displayNames.length - 1]} phone number)`;
}

/**
 * Parses `value` as a phone number, treating the first entry of `allowedCountries` as the default
 * region (used for bare national numbers without a `+` prefix). Returns the parsed number iff it
 * is valid AND its detected country is in `allowedCountries`; otherwise returns `undefined`.
 *
 * Note: National-format numbers (e.g. `4165551234` without `+`) are only interpreted using the
 * first allowed country as the default region. A valid Canadian national number will be rejected
 * when `allowedCountries: ['US', 'CA']` because it defaults to US parsing. The flow builder UI
 * requires international format (`+1-###-###-####`), so this is expected behavior.
 */
export function parseAllowedPhoneNumber(
  value: string,
  allowedCountries: CountryCode[]
): PhoneNumber | undefined {
  if (allowedCountries.length === 0) {
    throw new Error('allowedCountries must contain at least one country code');
  }
  const parsed = parsePhoneNumberFromString(value, allowedCountries[0]);
  if (!parsed?.isValid() || !parsed.country || !allowedCountries.includes(parsed.country)) {
    return undefined;
  }
  return parsed;
}
